import { useMemo } from 'react'

import { Color, theme } from '../../../../constants'
import { BadgeColor, IconDesign, IconName } from '../../../../elements'
import { useTranslations } from '../../../../translate'

export type CellStatus =
  | 'DRAFT'
  | 'VACANT'
  | 'ARCHIVED'
  | 'CANCELLED'
  | 'PRESENTED'
  | 'APPLIED'
  | 'ACCEPTED'
  | 'SUGGESTED'
  | 'BOOKED'
  | 'FLEX'
  | 'SELECTED'
  | 'REPLACING'
  | 'OCCUPIED'
  | 'BLOCKED'
  | 'REJECTED'
  | 'PRESET'
  | 'ACTIVE_PRESET'

type CellProps = {
  background: Color
  hoverBackground?: Color
  color: Color
  opacity?: number
  label: string
  subtitleOpacity: number
  border?: string
  icon?: IconName
  badgeColor?: BadgeColor
  iconProps: {
    name: IconName
    color: Color | string
    design?: IconDesign
  }
}

type CellTheme = Record<CellStatus, CellProps>

export const useCellTheme = () => {
  const t = useTranslations()

  const themes: CellTheme = useMemo(
    () => ({
      DRAFT: {
        background: Color.WHITE,
        color: Color.DARK_GREY,
        subtitleOpacity: 0.65,
        label: t.shifts.draft(),
        border: `1px dashed ${Color.MEDIUM_GREY}`,
        iconProps: {
          name: 'EmptyCircle',
          color: Color.LIGHT_GREY,
        },
      },
      VACANT: {
        background: Color.WHITE,
        color: Color.DARK_GREY,
        subtitleOpacity: 0.65,
        label: t.shifts.vacant_one(),
        border: `1px solid ${Color.BLUE}`,
        iconProps: {
          name: 'EmptyCircle',
          color: Color.DARK_GREY,
        },
      },
      ARCHIVED: {
        background: Color.TRANSPARENT,
        color: Color.DARK_RED,
        subtitleOpacity: 0.85,
        label: t.shifts.archived(),
        border: `1px dashed ${Color.DARK_RED}`,
        iconProps: {
          name: 'ArchiveIcon',
          color: Color.MEDIUM_DARK_RED,
          design: 'solid',
        },
      },
      CANCELLED: {
        background: Color.LIGHTEST_RED,
        color: Color.DARK_RED,
        subtitleOpacity: 0.8,
        label: t.shifts.cancelled(),
        border: `1px solid ${Color.LIGHTEST_RED}`,
        iconProps: {
          name: 'XCircleIcon',
          color: Color.RED,
          design: 'solid',
        },
      },
      PRESENTED: {
        background: Color.YELLOW,
        color: Color.CHARCOAL,
        label: t.shifts.presented(),
        subtitleOpacity: 0.65,
        badgeColor: 'yellow',
        iconProps: {
          name: 'FlagIcon',
          color: Color.MEDIUM_YELLOW,
          design: 'solid',
        },
      },
      APPLIED: {
        background: Color.YELLOW,
        color: Color.CHARCOAL,
        label: t.shifts.applied(),
        subtitleOpacity: 0.65,
        iconProps: {
          name: 'InProgress',
          color: Color.MEDIUM_YELLOW,
        },
      },
      ACCEPTED: {
        background: Color.LIGHTER_GREEN,
        color: Color.DARKEST_GREEN,
        subtitleOpacity: 0.8,
        label: t.shifts.accepted(),
        badgeColor: 'dark-green',
        iconProps: {
          name: 'CheckIcon',
          color: Color.GREEN,
        },
      },
      SUGGESTED: {
        background: Color.YELLOW,
        color: Color.DARK_YELLOW,
        label: t.shifts.suggested(),
        subtitleOpacity: 0.65,
        iconProps: {
          name: 'QuestionMarkCircleIcon',
          color: Color.MEDIUM_YELLOW,
          design: 'solid',
        },
      },
      BOOKED: {
        background: Color.GREEN,
        color: Color.WHITE,
        label: t.shifts.booked(),
        subtitleOpacity: 0.8,
        badgeColor: 'lighter-green',
        iconProps: {
          name: 'CheckCircleIcon',
          color: Color.GREEN,
          design: 'solid',
        },
      },
      FLEX: {
        background: Color.YELLOW,
        color: Color.CHARCOAL,
        label: t.shifts.flex_shift(),
        subtitleOpacity: 0.65,
        badgeColor: 'yellow',
        iconProps: {
          name: 'ClockIcon',
          color: Color.MEDIUM_YELLOW,
          design: 'solid',
        },
      },
      SELECTED: {
        background: Color.BLUE,
        color: Color.WHITE,
        label: t.shifts.selected(),
        subtitleOpacity: 0.9,
        icon: 'CheckCircleIcon',
        badgeColor: 'blue',
        iconProps: {
          name: 'CheckCircleIcon',
          color: Color.BLUE,
        },
      },
      REPLACING: {
        background: Color.YELLOW,
        color: Color.DARK_YELLOW,
        label: t.shifts.replacing(),
        subtitleOpacity: 0.65,
        icon: 'SwitchHorizontalIcon',
        iconProps: {
          name: 'SwitchHorizontalIcon',
          color: Color.DARK_YELLOW,
        },
      },
      OCCUPIED: {
        background: Color.MEDIUM_GREY,
        color: Color.CHARCOAL,
        label: t.shifts.occupied(),
        subtitleOpacity: 0.65,
        iconProps: {
          name: 'LockClosedIcon',
          color: Color.CHARCOAL,
        },
      },
      BLOCKED: {
        background: Color.MEDIUM_GREY,
        color: Color.CHARCOAL,
        label: t.shifts.blocked(),
        subtitleOpacity: 0.65,
        iconProps: {
          name: 'LockClosedIcon',
          color: Color.CHARCOAL,
        },
      },
      REJECTED: {
        background: Color.LIGHTEST_RED,
        color: Color.DARK_RED,
        subtitleOpacity: 0.8,
        label: t.shifts.rejected(),
        border: `1px solid ${Color.LIGHTEST_RED}`,
        badgeColor: 'dark-red',
        iconProps: {
          name: 'XIcon',
          color: Color.DARK_RED,
        },
      },
      PRESET: {
        background: Color.TRANSPARENT,
        hoverBackground: Color.LIGHT_GREY,
        color: Color.DARKER_GREY,
        border: theme.border.style.thinDark,
        label: t.shifts.template(),
        subtitleOpacity: 0.65,
        iconProps: {
          name: 'TemplateIcon',
          color: Color.BLUE,
        },
      },
      ACTIVE_PRESET: {
        background: Color.LIGHTEST_BLUE,
        color: Color.BLUE,
        border: `1px solid ${Color.BLUE}`,
        label: t.shifts.selected(),
        subtitleOpacity: 0.65,
        icon: 'CheckIcon',
        badgeColor: 'dark-blue',
        iconProps: {
          name: 'TemplateIcon',
          color: Color.BLUE,
        },
      },
    }),
    [t],
  )

  return themes
}
